import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation } from "@apollo/client";
import gql from 'graphql-tag';

import { useAppState } from "../../../contexts/AppContext";
import { FLAG_ZITADEL_AZURE_LOGIN, FLAG_ZITADEL_GOOGLE_LOGIN } from "../../../includes/featureFlags";
import { hasFeatureFlag } from "../../../includes/site";
import { AUTH_URL, getUrl, zitadelRoutes } from "../../../includes/routes";
import { API_BASE_URL } from "../../../../config/config";
import { FAILED_AUTHENTICATION_PARAM } from "../../../includes/constants";
import { LoginInfoManager } from "../../../includes/loginInfoManager";
import { useZitadel, ZitadelSession } from "../../../contexts/ZitadelContext";

type ZitadelLoginWithIdpMutationResponse = {
    zitadelLoginWithIdp: {
        user?: {
            id: string;
        };
        session?: ZitadelSession;
        isMfaRegistrationRequired: boolean;
        isTotpChallengeRequired: boolean;
        isOtpEmailChallengeRequired: boolean;
    },
}

type ZitadelLoginWithIdpMutationVariables = {
    intentId: string;
    intentToken: string;
    schoolUniqueKey: string;
}

const IdpLanding = () => {
    const navigate = useNavigate();

    const [searchParams] = useSearchParams();
    const intentId = searchParams.get(zitadelRoutes.IDP_LANDING.params.INTENT_ID) ?? "";
    const intentToken = searchParams.get(zitadelRoutes.IDP_LANDING.params.INTENT_TOKEN) ?? "";
    const isRedirectDisabled = searchParams.has(zitadelRoutes.IDP_LANDING.params.IS_REDIRECT_DISABLED);
    const backUrl = searchParams.get(zitadelRoutes.IDP_LANDING.params.BACK_URL) ?? "";
    const schoolUniqueKey = searchParams.get(zitadelRoutes.IDP_LANDING.params.SCHOOL_UNIQUE_KEY) ?? "";

    const isAuthenticationInFlight = useRef(false);
    const [isAuthenticationComplete, setIsAuthenticationComplete] = useState(false);

    const [appState] = useAppState();
    const isFeatureFlagsLoading = appState.isFeatureFlagsLoading;
    const hasZitadelSsoLoginFlag =
        hasFeatureFlag(appState.featureFlags, FLAG_ZITADEL_AZURE_LOGIN) ||
        hasFeatureFlag(appState.featureFlags, FLAG_ZITADEL_GOOGLE_LOGIN);

    const { setLoginInfo, setSession, clearSession } = useZitadel();

    const [zitadelLoginWithIdp] =
        useMutation<ZitadelLoginWithIdpMutationResponse, ZitadelLoginWithIdpMutationVariables>(ZITADEL_LOGIN_WITH_IDP);

    useEffect(function authenticateAndRedirectUser() {
        if (isFeatureFlagsLoading) return;
        if (!hasZitadelSsoLoginFlag) {
            navigate(getUrl(AUTH_URL));
            return;
        }

        if (!intentId || !intentToken) {
            LoginInfoManager.clear();
            navigate(`${getUrl(AUTH_URL)}?${FAILED_AUTHENTICATION_PARAM}`);
            return;
        }

        /** Strict prevention of accidental double ups */
        if (isAuthenticationInFlight.current) return;
        isAuthenticationInFlight.current = true;

        zitadelLoginWithIdp({
            variables: {
                intentId,
                intentToken,
                schoolUniqueKey,
            },
            fetchPolicy: 'network-only',
        }).then(({ data }) => {
            if (!data) throw new Error("Login attempt did not return a valid response.");
            const session = data.zitadelLoginWithIdp.session;
            if (!session) throw new Error("Login attempt did not return a valid response.");

            const isMfaRegistrationRequired = data.zitadelLoginWithIdp.isMfaRegistrationRequired;
            const isTotpChallengeRequired = data.zitadelLoginWithIdp.isTotpChallengeRequired;
            const isOtpEmailChallengeRequired = data.zitadelLoginWithIdp.isOtpEmailChallengeRequired;

            /** Redirect to MFA routes when necessary */
            if (isMfaRegistrationRequired || isTotpChallengeRequired || isOtpEmailChallengeRequired) {
                setLoginInfo({
                    isMfaRegistrationRequired,
                    isTotpChallengeRequired,
                    isOtpEmailChallengeRequired,
                });
                setSession(session);

                let url;
                if (isMfaRegistrationRequired) {
                    url = getUrl(zitadelRoutes.MFA_INITIALISATION.route);
                } else if (isTotpChallengeRequired) {
                    url = getUrl(zitadelRoutes.TOTP_CHALLENGE.route);
                } else if (isOtpEmailChallengeRequired) {
                    url = getUrl(zitadelRoutes.OTP_EMAIL_CHALLENGE.route);
                } else {
                    throw new Error("Login attempt did not return a valid response.");
                }
                setIsAuthenticationComplete(true);
                navigate(url);
                return;
            }

            setIsAuthenticationComplete(true);

            if (!isRedirectDisabled) {
                let url = API_BASE_URL + "/auth/login-redirect";
                if (backUrl) url += "?BackURL=" + backUrl;
                window.location.href = url;
            }
        }).catch(() => {
            LoginInfoManager.clear();
            clearSession();
            navigate(`${getUrl(AUTH_URL)}?${FAILED_AUTHENTICATION_PARAM}`);
        })
    }, [
        backUrl,
        clearSession,
        hasZitadelSsoLoginFlag,
        intentId,
        intentToken,
        isFeatureFlagsLoading,
        isRedirectDisabled,
        navigate,
        schoolUniqueKey,
        setLoginInfo,
        setSession,
        zitadelLoginWithIdp,
    ]);

    return (
        <div>
            {isAuthenticationComplete ?
                "Authentication complete!" + (isRedirectDisabled ? " You may return to your window" : "") :
                "Completing authentication..."
            }
        </div>
    );
};

const ZITADEL_LOGIN_WITH_IDP = gql`
    mutation zitadelLoginWithIdp($intentId: String!, $intentToken: String!, $schoolUniqueKey: String!) {
        zitadelLoginWithIdp(intentId: $intentId, intentToken: $intentToken, schoolUniqueKey: $schoolUniqueKey) {
            user {
                id
            }
            session {
                id
                token
                metadata {
                    userEmail
                }
            }
            isMfaRegistrationRequired
            isTotpChallengeRequired
            isOtpEmailChallengeRequired
        }
    }
`;

export default IdpLanding;
